import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Chip, CircularProgress, Stack, Tooltip, Typography } from "@mui/material";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, BarChart, Bar, Cell, PieChart, Pie } from "recharts";
import { ListChecks, CircleDot, CheckCircle2, AlertTriangle, UserX, Bug, Sparkles, Activity as ActivityIcon, Timer, Tag } from "lucide-react";
import { CardHeader, GlassCard, StatRow, StatTile, T, TYPE, CHART, SERIES, ChartTooltip, formatDate, formatRelative, labelSx, primaryButtonSx } from "../../user/complaints/shared";
import { pm, errorMessage } from "../api";
import { usePm } from "../PmContext";
import { HEALTH, Progress, ACTIVITY_TEXT, has } from "../ui";

const AiSummary = ({ project }) => {
  const ctx = usePm();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  if (!ctx.settings.ai?.enabled || !has(project, "ai.use")) return null;
  const run = async () => {
    setBusy(true);
    setError("");
    try {
      setData(await pm.projectAi(project._id));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const g = data?.generated || {};
  return (
    <GlassCard sx={{ mb: 2.5, borderColor: "#E9D7FE" }}>
      <CardHeader icon={Sparkles} title="AI project summary" subtitle="Generated from project data. Counts below the summary are measured, not generated."
        action={<Button size="small" variant="contained" sx={primaryButtonSx} onClick={run} disabled={busy}>{busy ? "Summarising…" : data ? "Refresh" : "Summarise"}</Button>} />
      {(busy || error || data) && (
        <Box sx={{ p: 2.5 }}>
          {busy && <CircularProgress size={18} />}
          {error && <Alert severity="warning">{error}</Alert>}
          {data && (
            <>
              <Typography sx={{ fontSize: TYPE.body, mb: 1.5 }}>{g.summary}</Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 2 }}>
                {[["completed", "Completed"], ["current", "In progress"], ["blocked", "Blocked"], ["risks", "Risks"], ["upcoming", "Upcoming"]].filter(([k]) => g[k]?.length).map(([k, label]) => (
                  <Box key={k}>
                    <Typography sx={{ ...labelSx, fontSize: "0.68rem" }}>{label}</Typography>
                    <ul style={{ margin: "4px 0", paddingLeft: 18, fontSize: 13 }}>{g[k].map((x, i) => <li key={i}>{x}</li>)}</ul>
                  </Box>
                ))}
              </Box>
              <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted, mt: 1 }}>
                Measured: {data.facts.totals?.open} open · {data.facts.overdue?.length} overdue · {data.facts.blocked?.length} blocked
              </Typography>
            </>
          )}
        </Box>
      )}
    </GlassCard>
  );
};

const Overview = ({ project, version }) => {
  const ctx = usePm();
  const [ov, setOv] = useState(null);
  const [health, setHealth] = useState(null);
  const [activity, setActivity] = useState([]);
  const [error, setError] = useState("");
  useEffect(() => {
    const canReport = has(project, "report.view");
    Promise.all([
      canReport ? pm.overview(project._id) : null,
      canReport ? pm.health(project._id).catch(() => null) : null,
      pm.activity(project._id, { limit: 12 }).then((r) => r.data).catch(() => []),
    ]).then(([o, h, a]) => {
      setOv(o);
      setHealth(h);
      setActivity(a);
    }).catch((err) => setError(errorMessage(err)));
  }, [project, version]);

  if (error) return <Alert severity="error">{error}</Alert>;
  const t = ov?.totals;
  const hs = health ? HEALTH[health.status] : null;

  return (
    <Box>
      {ov && (
        <StatRow columns={6}>
          <StatTile label="Total issues" value={t.total} icon={ListChecks} sub={`${ov.progressPct}% complete`} />
          <StatTile label="Open" value={t.open} icon={CircleDot} tone="info" sub={`${t.inProgress} in progress`} />
          <StatTile label="Done" value={t.done} icon={CheckCircle2} tone="good" />
          <StatTile label="Overdue" value={t.overdue} icon={AlertTriangle} tone={t.overdue ? "critical" : "good"} />
          <StatTile label="Unassigned" value={t.unassigned} icon={UserX} tone={t.unassigned ? "warning" : "good"} />
          <StatTile label="Bugs" value={t.bugs} icon={Bug} tone="critical" sub={`${t.stories} stories · ${t.tasks} tasks`} />
        </StatRow>
      )}
      <AiSummary project={project} />
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "2fr 1fr" }, gap: 2.5 }}>
        <Stack spacing={2.5} sx={{ minWidth: 0 }}>
          {ov && (
            <GlassCard>
              <CardHeader title="Created vs resolved" subtitle="Last 30 days" />
              <Box sx={{ height: 240, p: 2 }}>
                {ov.createdVsResolved.length ? (
                  <ResponsiveContainer>
                    <LineChart data={ov.createdVsResolved}>
                      <CartesianGrid stroke={CHART.grid} vertical={false} />
                      <XAxis dataKey="date" tick={CHART.tick} {...CHART.axisProps} tickFormatter={(d) => d.slice(5)} />
                      <YAxis tick={CHART.tick} {...CHART.axisProps} allowDecimals={false} width={30} />
                      <RTooltip content={<ChartTooltip />} />
                      <Line type="monotone" dataKey="created" stroke={SERIES[0]} strokeWidth={2} dot={false} name="Created" />
                      <Line type="monotone" dataKey="resolved" stroke={SERIES[2]} strokeWidth={2} dot={false} name="Resolved" />
                    </LineChart>
                  </ResponsiveContainer>
                ) : <Typography sx={{ fontSize: TYPE.small, color: T.inkMuted }}>No issues created or resolved in the last 30 days.</Typography>}
              </Box>
            </GlassCard>
          )}
          {ov && (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2.5 }}>
              <GlassCard>
                <CardHeader title="Issues by status" />
                <Box sx={{ height: 220, p: 1 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={ov.byStatus.map((s) => ({ name: ctx.statusById.get(String(s.statusId))?.name, value: s.count, color: ctx.statusById.get(String(s.statusId))?.color }))} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2}>
                        {ov.byStatus.map((s) => <Cell key={s.statusId} fill={ctx.statusById.get(String(s.statusId))?.color || T.inkMuted} />)}
                      </Pie>
                      <RTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
                <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} sx={{ px: 2, pb: 2 }}>
                  {ov.byStatus.map((s) => <Chip key={s.statusId} size="small" label={`${ctx.statusById.get(String(s.statusId))?.name}: ${s.count}`} sx={{ height: 22, fontSize: TYPE.micro }} />)}
                </Stack>
              </GlassCard>
              <GlassCard>
                <CardHeader title="Workload" subtitle="Open issues per person" />
                <Box sx={{ height: 260, p: 2 }}>
                  <ResponsiveContainer>
                    <BarChart data={ov.workload.slice(0, 8)} layout="vertical" margin={{ left: 10 }}>
                      <CartesianGrid stroke={CHART.grid} horizontal={false} />
                      <XAxis type="number" tick={CHART.tick} {...CHART.axisProps} allowDecimals={false} />
                      <YAxis type="category" dataKey="name" tick={CHART.tick} {...CHART.axisProps} width={100} />
                      <RTooltip content={<ChartTooltip />} />
                      <Bar dataKey="open" fill={SERIES[0]} radius={[0, 4, 4, 0]} name="Open" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </GlassCard>
            </Box>
          )}
        </Stack>
        <Stack spacing={2.5} sx={{ minWidth: 0 }}>
          {health && (
            <GlassCard>
              <CardHeader title="Project health" subtitle="Measured against thresholds in Project Settings" action={<Chip label={hs.label} size="small" sx={{ bgcolor: hs.bg, color: hs.fg, fontWeight: 700 }} />} />
              <Box sx={{ p: 2 }}>
                {health.metrics.map((m) => (
                  <Tooltip key={m.key} title={m.rule} placement="left">
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.75 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: HEALTH[m.status].fg }} />
                      <Typography sx={{ fontSize: TYPE.small, flexGrow: 1 }}>{m.label}</Typography>
                      <Typography sx={{ fontSize: TYPE.small, fontWeight: 700 }}>{m.value}</Typography>
                      {m.unit && <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>{m.unit}</Typography>}
                    </Stack>
                  </Tooltip>
                ))}
              </Box>
            </GlassCard>
          )}
          {ov?.sprint && (
            <GlassCard>
              <CardHeader icon={Timer} title={ov.sprint.name} subtitle={`Ends ${formatDate(ov.sprint.endDate)}`} />
              <Box sx={{ p: 2 }}>
                {ov.sprint.goal && <Typography sx={{ fontSize: TYPE.small, color: T.inkSecondary, mb: 1.25 }}>{ov.sprint.goal}</Typography>}
                <Progress value={ov.sprint.pct} label={`${ov.sprint.done}/${ov.sprint.issues} issues · ${ov.sprint.donePoints}/${ov.sprint.points} pts`} color="#12B76A" />
              </Box>
            </GlassCard>
          )}
          {ov?.release && (
            <GlassCard>
              <CardHeader icon={Tag} title={`Next release: ${ov.release.name}`} subtitle={ov.release.releaseDate ? `Planned ${formatDate(ov.release.releaseDate)}` : "No date set"} />
              <Box sx={{ p: 2 }}><Progress value={ov.release.pct} label={`${ov.release.done}/${ov.release.issues} issues done`} /></Box>
            </GlassCard>
          )}
          <GlassCard>
            <CardHeader icon={ActivityIcon} title="Recent activity" />
            <Box sx={{ p: 2 }}>
              {!activity.length && <Typography sx={{ fontSize: TYPE.small, color: T.inkMuted }}>Nothing yet.</Typography>}
              {activity.map((a) => (
                <Box key={a._id} sx={{ py: 0.75, borderBottom: `1px solid ${T.border}`, "&:last-child": { borderBottom: 0 } }}>
                  <Typography sx={{ fontSize: TYPE.small }}>{ACTIVITY_TEXT(a)}</Typography>
                  <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>{formatRelative(a.createdAt)}</Typography>
                </Box>
              ))}
            </Box>
          </GlassCard>
          <GlassCard sx={{ p: 2 }}>
            <Typography sx={{ ...labelSx, mb: 1 }}>Details</Typography>
            <Typography sx={{ fontSize: TYPE.small }}>Lead: <b>{project.lead?.name}</b></Typography>
            {project.manager && <Typography sx={{ fontSize: TYPE.small }}>Manager: <b>{project.manager.name}</b></Typography>}
            <Typography sx={{ fontSize: TYPE.small }}>Status: {project.status} · {project.visibility}</Typography>
            <Typography sx={{ fontSize: TYPE.small }}>{project.startDate ? formatDate(project.startDate) : "No start"} → {project.endDate ? formatDate(project.endDate) : "No end date"}</Typography>
            <Typography sx={{ fontSize: TYPE.small }}>{project.members?.length} members · estimation in {project.estimation}</Typography>
          </GlassCard>
        </Stack>
      </Box>
    </Box>
  );
};

export default Overview;
